/**
 * Money primitives.
 *
 *   - `Shekels` — whole shekels. Prayer orders and user balances.
 *   - `Money`   — shekels with up to 2 decimals (agorot). Synagogue
 *                 transactions and the synagogue balance only. See below.
 *
 * Order money passes through parseShekels at the system
 * boundary (HTTP body, form input, DB read). Once a value has the Shekels
 * brand, downstream code (ledger.ts, route handlers) can trust it.
 *
 * Why a brand: prevents accidentally adding a raw user-input number to a
 * Shekels-typed balance without going through validation.
 */

declare const ShekelsBrand: unique symbol;
export type Shekels = number & { readonly [ShekelsBrand]: true };

export class InvalidShekelsError extends Error {
  constructor(public readonly input: unknown, reason: string) {
    super(`Invalid shekel value: ${reason} (got ${JSON.stringify(input)})`);
    this.name = 'InvalidShekelsError';
  }
}

/**
 * Parse any input into Shekels. Strict: rejects decimals, NaN, Infinity,
 * scientific notation, non-digit strings. Accepts negatives (a balance can
 * be in debt). Trims surrounding whitespace on string input.
 *
 * Use the schema-level .min(0) refinement to forbid negatives on fields
 * that must be non-negative (price, pricePaid, amountPaid).
 */
export function parseShekels(input: unknown): Shekels {
  if (input === null || input === undefined) {
    throw new InvalidShekelsError(input, 'null or undefined');
  }

  if (typeof input === 'number') {
    if (!Number.isFinite(input)) {
      throw new InvalidShekelsError(input, 'not finite');
    }
    if (!Number.isInteger(input)) {
      throw new InvalidShekelsError(input, 'has fractional part');
    }
    if (!Number.isSafeInteger(input)) {
      throw new InvalidShekelsError(input, 'outside safe integer range');
    }
    return input as Shekels;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (trimmed === '') {
      throw new InvalidShekelsError(input, 'empty string');
    }
    // Strict integer pattern. Rejects "20.0", "2e3", "0x10", "20abc", "++20".
    if (!/^-?\d+$/.test(trimmed)) {
      throw new InvalidShekelsError(input, 'not a whole number');
    }
    const n = Number(trimmed);
    if (!Number.isSafeInteger(n)) {
      throw new InvalidShekelsError(input, 'outside safe integer range');
    }
    return n as Shekels;
  }

  throw new InvalidShekelsError(input, `unsupported type ${typeof input}`);
}

/**
 * Type guard / runtime assertion. Throws if value is not a valid Shekels.
 * Use at API boundaries when you have a value typed as `number` but want
 * to promote it to `Shekels`.
 */
export function assertShekels(value: number): asserts value is Shekels {
  parseShekels(value);
}

/**
 * Construct Shekels from a value already known to be a valid integer.
 * Use only when the value was just produced by ledger math (which is
 * closed over Shekels) and you want to re-narrow after a TS widening.
 * Do NOT use to bypass validation of user input.
 */
export function unsafeShekels(value: number): Shekels {
  return value as Shekels;
}

/**
 * Lenient coercion for legacy DB reads.
 *
 * Returns a valid integer Shekels for any input by:
 *   - returning 0 for null / undefined / NaN / Infinity / non-numeric strings
 *   - rounding floats to the nearest integer
 *   - clamping outside-safe-integer-range to 0
 *
 * NEVER THROWS. Intended for reading legacy values that may predate the
 * strict integer schemas. **Do not use on client-supplied input** — for
 * that, use `parseShekels` so bad inputs surface as 400 errors at the
 * API boundary.
 *
 * Rounds half-up via Math.round, matching the human-intuitive expectation
 * that 49.5 → 50 (not banker's rounding).
 */
export function coerceShekels(value: unknown): Shekels {
  if (value === null || value === undefined) return ZERO_SHEKELS;

  let n: number;
  if (typeof value === 'number') {
    n = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return ZERO_SHEKELS;
    n = Number(trimmed);
  } else {
    return ZERO_SHEKELS;
  }

  if (!Number.isFinite(n)) return ZERO_SHEKELS;
  const rounded = Math.round(n);
  if (!Number.isSafeInteger(rounded)) return ZERO_SHEKELS;
  return rounded as Shekels;
}

/**
 * Display formatter. Returns e.g. "₪1,234" or "-₪50".
 * Locale-aware (he-IL grouping).
 */
export function formatShekels(value: Shekels): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const grouped = abs.toLocaleString('he-IL', { maximumFractionDigits: 0 });
  return `${sign}₪${grouped}`;
}

export const ZERO_SHEKELS = 0 as Shekels;

// ─────────────────────────────────────────────────────────────────────────────
// Synagogue money — shekels with up to 2 decimal places (agorot).
//
// Prayer orders stay whole shekels (everything above). Synagogue income and
// expenses come from bank statements and bills, so they carry agorot
// (e.g. ₪227,065.90).
//
// DB: stored exactly as before — a plain number of shekels in the same
// fields (amountPaid / transactionAmount / balance), now allowed up to 2
// decimals. No new property, no migration: whole numbers are still valid.
//
// All arithmetic runs on integer agorot, so float noise can never creep
// into a balance (0.1 + 0.2 → 0.30000000000000004 cannot happen here).
// ─────────────────────────────────────────────────────────────────────────────

declare const MoneyBrand: unique symbol;
/** Shekels with at most 2 decimal places. Synagogue transactions only. */
export type Money = number & { readonly [MoneyBrand]: true };

export class InvalidMoneyError extends Error {
  constructor(public readonly input: unknown, reason: string) {
    super(`Invalid money value: ${reason} (got ${JSON.stringify(input)})`);
    this.name = 'InvalidMoneyError';
  }
}

/** Keeps agorot arithmetic inside the safe-integer range. */
const MAX_ABS_MONEY = Math.floor(Number.MAX_SAFE_INTEGER / 100);

/**
 * Shekels → integer agorot. `toFixed(6)` strips float noise before rounding,
 * so 983.035 → 98303.5 → 98304 (instead of 98303.49999999999 → 98303).
 * Halves round away from zero, symmetrically for negatives.
 */
export function toAgorot(shekels: number): number {
  const r = Math.round(Number((Math.abs(shekels) * 100).toFixed(6)));
  return shekels < 0 && r !== 0 ? -r : r;
}

/** Integer agorot → Money (the nearest double to the exact 2-decimal value). */
export function fromAgorot(agorot: number): Money {
  return (agorot / 100) as Money;
}

/**
 * True when `n` is exactly what parsing a ≤2-decimal string would give
 * (227065.9 ✓, 983.035 ✗, NaN ✗). Exact — no epsilon guesswork.
 */
export function hasAtMostTwoDecimals(n: number): boolean {
  if (!Number.isFinite(n) || Math.abs(n) > MAX_ABS_MONEY) return false;
  return Number(n.toFixed(2)) === n;
}

/**
 * Strict parse for synagogue amounts on write. Accepts a finite number or
 * a plain decimal string ("1234", "1234.5", "1234.50"). Rejects more than
 * 2 decimal places, NaN/Infinity, exponents, thousands separators and junk.
 * Negatives are allowed (a running balance can dip below zero); schemas
 * enforce > 0 on fields that need it.
 */
export function parseMoney(input: unknown): Money {
  if (input === null || input === undefined) {
    throw new InvalidMoneyError(input, 'null or undefined');
  }

  let n: number;
  if (typeof input === 'number') {
    n = input;
  } else if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) {
      throw new InvalidMoneyError(input, 'not an amount with up to 2 decimals');
    }
    n = Number(trimmed);
  } else {
    throw new InvalidMoneyError(input, `unsupported type ${typeof input}`);
  }

  if (!Number.isFinite(n)) throw new InvalidMoneyError(input, 'not finite');
  if (Math.abs(n) > MAX_ABS_MONEY) throw new InvalidMoneyError(input, 'too large');
  if (!hasAtMostTwoDecimals(n)) {
    throw new InvalidMoneyError(input, 'more than 2 decimal places');
  }
  return fromAgorot(toAgorot(n)); // normalises -0 → 0
}

/**
 * Lenient coercion for DB reads (legacy rows): junk / null → 0, otherwise
 * rounded to the nearest agora. NEVER THROWS. Do not use on client input —
 * use `parseMoney` there so bad values surface as 400s.
 */
export function coerceMoney(value: unknown): Money {
  let n: number;
  if (typeof value === 'number') {
    n = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    n = Number(value.trim());
  } else {
    return ZERO_MONEY;
  }
  if (!Number.isFinite(n) || Math.abs(n) > MAX_ABS_MONEY) return ZERO_MONEY;
  return fromAgorot(toAgorot(n));
}

export function addMoney(a: Money, b: Money): Money {
  return fromAgorot(toAgorot(a) + toAgorot(b));
}

export function subtractMoney(a: Money, b: Money): Money {
  return fromAgorot(toAgorot(a) - toAgorot(b));
}

/**
 * Display formatter: "₪1,234" for whole amounts, "₪1,234.50" with agorot,
 * "-₪50" when negative.
 */
export function formatMoney(value: Money): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const hasAgorot = toAgorot(abs) % 100 !== 0;
  const grouped = abs.toLocaleString('he-IL', {
    minimumFractionDigits: hasAgorot ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `${sign}₪${grouped}`;
}

export const ZERO_MONEY = 0 as Money;
